import { XAI_CHAT_URL, extractJsonPayload } from "./inbox";
import { normalizeIcaoCode } from "./company";
import { SUGGESTED_COMPANY_NAME, SUGGESTED_HOME_ICAO } from "./profile";
import { lookupAirport } from "./airports";
import { DEFAULT_OPENING_BALANCE_CAD } from "./ledger";
import { normalizeFleetRegistration, SUGGESTED_PRIMARY_REG, type CareerFleetRow, type CareerWizardDraft } from "./career";
import type { FlightType } from "./types";

/** Same cheap structured model as Inbox. Only called when Settings already has a key. */
export const XAI_CAREER_MODEL = "grok-3-mini";

export const BEGINNING_SCENARIO_PLACEHOLDER =
  "Opening ferry. Enter the aircraft, tail, airports, route, and times.";

/** Default opening-ferry origin (Beech Factory / Wichita). Not KPEC. */
export const DEFAULT_FERRY_START_ICAO = "KBEC";

/** Blank wizard text stays blank. Nothing is invented for the opening ferry. */
export function effectiveBeginningScenarioText(text: string | null | undefined): string {
  return text?.trim() ?? "";
}

export type CareerSeedJob = {
  kind: "ferry" | "charter";
  originIcao: string;
  destIcao: string;
  summary: string;
  flightType: FlightType;
};

export type CareerSeed = {
  source: "grok" | "local";
  hqIcao: string;
  startIcao: string;
  operatorName: string;
  startingCapitalCad: number;
  primaryRegistration: string;
  primaryAircraftId: string;
  firstJob: CareerSeedJob;
  notes: string;
  rawText: string;
};

export type ProposeCareerSeedInput = {
  text: string;
  wizard: Pick<
    CareerWizardDraft,
    "operatorName" | "homeIcao" | "startingCapitalCad" | "fleetRows" | "adminName"
  >;
  apiKey?: string;
};

export type ProposeCareerSeedResult =
  | { ok: true; seed: CareerSeed; usedGrok: boolean }
  | { ok: false; error: string };

const FROM_TO = /\bfrom\s+([A-Za-z][A-Za-z0-9]{3})\s+to\s+(home(?:\s+base)?|[A-Za-z][A-Za-z0-9]{3})\b/i;
const REG_RE = /\b([A-Z]-?[A-Z0-9]{3,5})\b/g;
const MONEY_RE = /\$\s*([\d,]+(?:\.\d+)?)/;

export function parseBeginningScenario(
  text: string,
  wizard: ProposeCareerSeedInput["wizard"],
): CareerSeed {
  const supplied = text.trim();
  const raw = supplied;
  const hq = normalizeIcaoCode(wizard.homeIcao) || SUGGESTED_HOME_ICAO;
  const fleet = wizard.fleetRows ?? [];
  if (!raw) {
    return {
      source: "local",
      hqIcao: hq,
      startIcao: "",
      operatorName: wizard.operatorName.trim(),
      startingCapitalCad:
        Number.isFinite(wizard.startingCapitalCad) && wizard.startingCapitalCad > 0
          ? Math.round(wizard.startingCapitalCad)
          : 0,
      primaryRegistration: "",
      primaryAircraftId: "",
      firstJob: {
        kind: "ferry",
        originIcao: "",
        destIcao: "",
        summary: "",
        flightType: "ferry",
      },
      notes: "",
      rawText: "",
    };
  }
  const primary = pickPrimary(raw, fleet);

  let start = hq;
  let dest = hq;
  const pair = raw.match(FROM_TO);
  if (pair) {
    start = normalizeIcaoCode(pair[1] ?? "") || start;
    const destRaw = (pair[2] ?? "").toLowerCase();
    dest = destRaw.startsWith("home") ? hq : normalizeIcaoCode(pair[2] ?? "") || hq;
  } else {
    const icaos = extractIcaos(raw).filter((code) => code !== hq);
    if (icaos[0]) start = icaos[0];
  }

  const ferry = /\bferry|newly acquired|reposition\b/i.test(raw) || start !== dest;
  const originIcao = start;
  const destIcao = dest === start ? hq : dest;
  const operatorName = wizard.operatorName.trim() || extractOperatorName(raw) || SUGGESTED_COMPANY_NAME;
  const capital = parseCapital(raw, wizard.startingCapitalCad);
  const summary = ferry
    ? `Ferry ${primary.registration} ${originIcao}–${destIcao} to open the career.`
    : `Opening charter ${originIcao}–${destIcao}.`;

  return {
    source: "local",
    hqIcao: hq,
    startIcao: start,
    operatorName,
    startingCapitalCad: capital,
    primaryRegistration: primary.registration,
    primaryAircraftId: primary.id,
    firstJob: {
      kind: ferry ? "ferry" : "charter",
      originIcao,
      destIcao,
      summary,
      flightType: ferry ? "ferry" : "corporate",
    },
    notes: "Parsed locally from the beginning-scenario text.",
    rawText: raw,
  };
}

/** Inbox / Flight Plans pair for the opening job. Uncollapses a ferry that was seeded home–home. */
export function openingJobPair(seed: Pick<CareerSeed, "hqIcao" | "startIcao" | "firstJob">): {
  originIcao: string;
  destIcao: string;
} {
  const origin = normalizeIcaoCode(seed.firstJob.originIcao) || normalizeIcaoCode(seed.startIcao);
  const dest = normalizeIcaoCode(seed.firstJob.destIcao);
  const start = normalizeIcaoCode(seed.startIcao);
  const hq = normalizeIcaoCode(seed.hqIcao);
  if (seed.firstJob.kind === "ferry" && origin && dest && origin === dest && start && hq && start !== hq) {
    return { originIcao: start, destIcao: hq };
  }
  return { originIcao: origin, destIcao: dest };
}

/** Prefer the local ferry pair when a Grok/merge pass collapses orig===dest. */
export function uncollapseOpeningFerry(seed: CareerSeed, fallback: CareerSeed): CareerSeed {
  const local = openingJobPair(fallback);
  const next = openingJobPair(seed);
  if (next.originIcao === next.destIcao && local.originIcao !== local.destIcao) {
    return {
      ...seed,
      startIcao: fallback.startIcao,
      firstJob: {
        ...seed.firstJob,
        kind: fallback.firstJob.kind,
        originIcao: local.originIcao,
        destIcao: local.destIcao,
        flightType: fallback.firstJob.flightType,
        summary: fallback.firstJob.summary,
      },
    };
  }
  return seed;
}

function pickPrimary(text: string, fleet: CareerFleetRow[]): { registration: string; id: string } {
  const hawker = /\bhawker|800xp|h25b\b/i.test(text);
  const pc12 = /\bpc-?12|pilatus\b/i.test(text);
  const regs = extractRegistrations(text);
  const byReg = regs
    .map((reg) => fleet.find((r) => normalizeFleetRegistration(r.registration) === reg))
    .find(Boolean);
  if (byReg) {
    return {
      registration: normalizeFleetRegistration(byReg.registration),
      id: fleetId(byReg),
    };
  }
  if (regs[0] && !lookupAirport(regs[0])) {
    return { registration: regs[0], id: fleetId(fleet[0]) };
  }
  const typed = fleet.find((r) =>
    hawker ? r.icaoType === "H25B" : pc12 ? r.icaoType === "PC12" : false,
  );
  const row = typed ?? fleet[0];
  if (!row) return { registration: "", id: "" };
  return {
    registration: normalizeFleetRegistration(row.registration),
    id: fleetId(row),
  };
}

function fleetId(row: CareerFleetRow | undefined): string {
  if (!row) return "hawker-800xp";
  if (row.icaoType === "H25B" && normalizeFleetRegistration(row.registration) === SUGGESTED_PRIMARY_REG) {
    return "hawker-800xp";
  }
  if (row.icaoType === "PC12") return "pc12-ng";
  return `ac-${normalizeFleetRegistration(row.registration).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
}

export function extractIcaos(text: string): string[] {
  const out: string[] = [];
  const re = /\b([A-Z][A-Z0-9]{3})\b/g;
  const upper = text.toUpperCase();
  let m: RegExpExecArray | null;
  while ((m = re.exec(upper))) {
    const code = m[1]!;
    if (lookupAirport(code) || /^[A-Z]{4}$/.test(code)) {
      if (!out.includes(code)) out.push(code);
    }
  }
  return out;
}

export function extractRegistrations(text: string): string[] {
  const out: string[] = [];
  const upper = text.toUpperCase();
  let m: RegExpExecArray | null;
  const re = new RegExp(REG_RE.source, "g");
  while ((m = re.exec(upper))) {
    const raw = normalizeFleetRegistration(m[1] ?? "");
    if (!raw || lookupAirport(raw) || extractIcaos(raw).includes(raw)) continue;
    if (/^[A-Z]-?[A-Z0-9]{3,5}$/.test(raw) && !out.includes(raw)) out.push(raw);
  }
  return out;
}

function extractOperatorName(text: string): string {
  const forMatch = text.match(/\bfor\s+([A-Z][\w'’]*(?:\s+[A-Z][\w'’]*){1,5})\b/);
  return forMatch?.[1]?.trim() ?? "";
}

function parseCapital(text: string, fallback: number): number {
  const hit = text.match(MONEY_RE);
  if (!hit) return fallback > 0 ? fallback : DEFAULT_OPENING_BALANCE_CAD;
  const n = Number((hit[1] ?? "").replace(/,/g, ""));
  return Number.isFinite(n) && n > 0 ? Math.round(n) : fallback;
}

export function mergeCareerProposal(wizard: ProposeCareerSeedInput["wizard"], parsed: CareerSeed): CareerSeed {
  const hq = normalizeIcaoCode(wizard.homeIcao) || parsed.hqIcao;
  const dest = parsed.firstJob.destIcao === parsed.hqIcao ? hq : parsed.firstJob.destIcao;
  const start = parsed.startIcao;
  const merged: CareerSeed = {
    ...parsed,
    hqIcao: hq,
    startIcao: start,
    operatorName: wizard.operatorName.trim() || parsed.operatorName,
    startingCapitalCad: wizard.startingCapitalCad > 0 ? wizard.startingCapitalCad : parsed.startingCapitalCad,
    firstJob: {
      ...parsed.firstJob,
      destIcao: dest,
    },
  };
  const pair = openingJobPair(merged);
  return {
    ...merged,
    firstJob: {
      ...merged.firstJob,
      originIcao: pair.originIcao,
      destIcao: pair.destIcao,
    },
  };
}

function buildGrokCareerPrompt(input: ProposeCareerSeedInput): string {
  const fleet = (input.wizard.fleetRows ?? [])
    .map((r) => `${r.registration} ${r.icaoType}`)
    .join(", ");
  return [
    "Propose a structured Northstar Dispatch career opening seed.",
    "Return ONLY JSON with keys: hqIcao, startIcao, operatorName, startingCapitalCad,",
    "primaryRegistration, firstJobKind (ferry|charter), firstJobOrigin, firstJobDest, summary.",
    "Do not invent airports that contradict the user text. Home / HQ is the company base.",
    `Company: ${input.wizard.operatorName} HQ ${input.wizard.homeIcao} capital ${input.wizard.startingCapitalCad}`,
    `Fleet: ${fleet || "unset"}`,
    `User text: ${effectiveBeginningScenarioText(input.text)}`,
  ].join("\n");
}

export async function callGrokCareerSeed(apiKey: string, input: ProposeCareerSeedInput): Promise<unknown> {
  const res = await fetch(XAI_CHAT_URL, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${apiKey}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      model: XAI_CAREER_MODEL,
      temperature: 0.2,
      messages: [
        { role: "system", content: "You write structured aviation charter seeds. JSON only." },
        { role: "user", content: buildGrokCareerPrompt(input) },
      ],
    }),
  });
  if (!res.ok) {
    throw new Error(`Grok career seed HTTP ${res.status}`);
  }
  const payload = (await res.json()) as { choices?: { message?: { content?: string } }[] };
  const content = payload.choices?.[0]?.message?.content;
  if (!content) throw new Error("Grok returned an empty career seed.");
  return extractJsonPayload(content);
}

function seedFromGrokJson(raw: unknown, fallback: CareerSeed): CareerSeed {
  if (!raw || typeof raw !== "object") return fallback;
  const o = raw as Record<string, unknown>;
  const str = (key: string, alt: string) => (typeof o[key] === "string" && o[key].trim() ? String(o[key]).trim() : alt);
  const kind = String(o.firstJobKind ?? fallback.firstJob.kind).toLowerCase() === "charter" ? "charter" : "ferry";
  const hq = normalizeIcaoCode(str("hqIcao", fallback.hqIcao)) || fallback.hqIcao;
  const start = normalizeIcaoCode(str("startIcao", fallback.startIcao)) || fallback.startIcao;
  const origin = normalizeIcaoCode(str("firstJobOrigin", fallback.firstJob.originIcao)) || start;
  const dest = normalizeIcaoCode(str("firstJobDest", fallback.firstJob.destIcao)) || hq;
  const capital = Number(o.startingCapitalCad);
  return {
    ...fallback,
    source: "grok",
    hqIcao: hq,
    startIcao: start,
    operatorName: str("operatorName", fallback.operatorName),
    startingCapitalCad: Number.isFinite(capital) && capital > 0 ? Math.round(capital) : fallback.startingCapitalCad,
    primaryRegistration: normalizeFleetRegistration(str("primaryRegistration", fallback.primaryRegistration)),
    firstJob: {
      kind,
      originIcao: origin,
      destIcao: dest,
      summary: str("summary", fallback.firstJob.summary),
      flightType: kind === "ferry" ? "ferry" : "corporate",
    },
    notes: "Proposed by Grok — confirm before apply.",
  };
}

/**
 * Desk-owned proposal. Never mutates company. Grok runs only when a Settings key
 * is already present; otherwise the local parser still returns a reviewable seed.
 */
export async function proposeBeginningScenario(input: ProposeCareerSeedInput): Promise<ProposeCareerSeedResult> {
  const local = mergeCareerProposal(input.wizard, parseBeginningScenario(input.text, input.wizard));
  const key = input.apiKey?.trim();
  if (!key) {
    return { ok: true, seed: local, usedGrok: false };
  }
  try {
    const raw = await callGrokCareerSeed(key, input);
    return { ok: true, seed: uncollapseOpeningFerry(seedFromGrokJson(raw, local), local), usedGrok: true };
  } catch {
    return { ok: true, seed: { ...local, notes: "Grok unavailable — local parser used." }, usedGrok: false };
  }
}
